import React from 'react';
import styled from 'styled-components/macro';
import { useApiRequest } from '../api/useApiRequest';
import { useOnMount } from '../helpers/hooks/useOnMount';
import { assertNotNull } from '../helpers/nullHelpers';
import { LinkButton } from '../shared/Button';
import { PageLoadingIndicator } from '../shared/LoadingIndicator';
import { fadeIn } from '../styling/animations';
import { spacing06 } from '../styling/spacing';
import { UserContext, UserDetails } from './UserContext';

type Props = {
  children?: React.ReactNode;
};

export const UserProvider = (props: Props) => {
  const getCurrentUserRequest = useApiRequest<UserDetails>({ httpMethod: 'get', endpointUrl: 'users/current-user' });

  useOnMount(() => {
    getCurrentUserRequest.sendRequest();
  });

  if (getCurrentUserRequest.inProgress) {
    return (
      <CenteredContainer>
        <PageLoadingIndicator />
      </CenteredContainer>
    );
  }

  if (getCurrentUserRequest.error != null) {
    return (
      <CenteredContainer>
        <div>
          <p>Sorry, something went wrong.</p>
          <RetryButtonContainer>
            <LinkButton onClick={() => getCurrentUserRequest.sendRequest()}>Try again</LinkButton>
          </RetryButtonContainer>
        </div>
      </CenteredContainer>
    );
  }

  if (getCurrentUserRequest.response == null) {
    return (
      <CenteredContainer>
        <PageLoadingIndicator />
      </CenteredContainer>
    );
  }

  const getUser = () => assertNotNull(getCurrentUserRequest.response);

  return <UserContext.Provider value={{ getUser }}>{props.children}</UserContext.Provider>;
};

const CenteredContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  animation: ${fadeIn} 0.5s ease-in both;
`;

const RetryButtonContainer = styled.div`
  margin-top: ${spacing06};
`;
